import { SyntheticEvent, useMemo } from 'react';
import { fieldToAutocomplete, AutocompleteProps } from 'formik-mui';
import { IManager } from '@sr/dto';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Box, Chip, Typography } from '@mui/material';
import { useManagersDropdownList } from 'features/user/user-picker';
import {
  filterHiddenManagers,
  filterManagerOptions,
  getManagerStatusSuffix,
  isToggleHiddenOption,
  renderManagerOption,
  splitToggleOptions,
  useHiddenManagerToggleState,
  withHiddenToggle
} from 'shared/ui/fired-manager-toggle';

export const UsersMultiPickerField = (
  props: AutocompleteProps<string | IManager, true, boolean, boolean>
) => {
  const { form, field } = props;
  const value = (field.value || []) as IManager[];
  const { items: users, isLoading } = useManagersDropdownList();

  const { showHidden, toggleShowHidden, hasHiddenManagers } =
    useHiddenManagerToggleState(users);

  const options = useMemo(() => {
    const visible = filterHiddenManagers(users, showHidden);
    const visibleIds = new Set(visible.map((m: IManager) => m.id));
    const selectedMissing = value.filter((v) => !visibleIds.has(v.id));
    const merged = [...selectedMissing, ...visible];

    return withHiddenToggle(merged, { hasHiddenManagers });
  }, [users, showHidden, hasHiddenManagers, value]);

  const handleDelete = (id: string) => {
    const newValue = value.filter((u) => u.id !== Number(id));
    form.setFieldValue(field.name, newValue);
  };

  const handleChange = (
    _event: SyntheticEvent,
    newValue: (string | IManager)[],
    reason: string
  ) => {
    const last = newValue[newValue.length - 1] as IManager | undefined;
    if (reason === 'selectOption' && isToggleHiddenOption(last)) {
      toggleShowHidden();
      return;
    }

    const cleaned = (newValue as IManager[]).filter(
      (v) => !isToggleHiddenOption(v)
    );
    form.setFieldValue(field.name, cleaned);
  };

  return (
    <Box>
      <Autocomplete
        {...fieldToAutocomplete(props)}
        options={options}
        value={value}
        onChange={handleChange}
        loading={isLoading}
        multiple
        disableCloseOnSelect
        renderTags={() => null}
        getOptionLabel={(option: string | IManager) =>
          isToggleHiddenOption(option as IManager)
            ? ''
            : `${(option as IManager).firstName} ${
              (option as IManager).secondName || ''
            }`.trim()
        }
        isOptionEqualToValue={(option, val) =>
          (option as IManager).id === (val as IManager).id
        }
        filterOptions={(opts, params) =>
          splitToggleOptions(
            opts as IManager[],
            params as Parameters<typeof filterManagerOptions>[1]
          )
        }
        renderOption={(optionProps, option) =>
          renderManagerOption(optionProps, option as IManager, {
            showHidden,
            onToggle: toggleShowHidden
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Сотрудники"
            placeholder={
              value.length ? `Выбрано: ${value.length}` : 'Выберите сотрудников'
            }
          />
        )}
      />

      {value.length > 0 && (
        <Box mt={1}>
          <Typography
            variant="caption"
            sx={{ opacity: 0.6, display: 'block', mb: 0.5 }}
          >
            Выбраны:
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
            {value.map((user) => {
              const suffix = getManagerStatusSuffix(user);
              return (
                <Chip
                  key={user.id}
                  label={
                    `${user.firstName} ${user.secondName || ''}`.trim() +
                    (suffix ? ` ${suffix}` : '')
                  }
                  onDelete={() => handleDelete(String(user.id))}
                  sx={suffix ? { color: 'text.disabled' } : undefined}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};